// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useMemo, useCallback, type ReactNode } from 'react';

import {
  useEvolveDelete,
  useEvolvePost,
  useEvolvePut,
  type HexTriplet,
  type ShipmentStatus,
  type ShipmentStatusId,
} from '@evolvemep/api-ts';

import type { CustomStatus, CustomStatusesHookValue, EntityState } from 'components/Mantine/StatusEditor/types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

type ShipmentStatusesContextType = CustomStatusesHookValue<'Shipment'>;

const normalizeStatus = (status: ShipmentStatus): CustomStatus<'Shipment'> => ({
  statusId: status.shipmentStatusId,
  stateId: status.shipmentStateId,
  name: status.name,
  color: status.color,
});

const ShipmentStatusesContext = createContext<CustomStatusesHookValue<'Shipment'> | undefined>(undefined);

export const ShipmentStatusesProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: shipmentStatesData,
    updateStoredValue: setShipmentStatesData,
    loading,
  } = useEvolveGetWithLocalStorage('shop/shipment/states');

  const states = useMemo<EntityState<'Shipment'>[] | undefined>(
    () =>
      shipmentStatesData?.map((state) => ({
        stateId: state.shipmentStateId,
        name: state.name,
        statuses: state.statuses.map(normalizeStatus),
      })),
    [shipmentStatesData],
  );

  const statuses = useMemo<CustomStatus<'Shipment'>[]>(
    () => states?.flatMap((state) => state.statuses) ?? [],
    [states],
  );

  const statusMap = useMemo<Record<ShipmentStatusId, CustomStatus<'Shipment'>>>(
    () => statuses.reduce((out, current) => ({ ...out, [current.statusId]: current }), {}),
    [statuses],
  );

  const { apiCall: createStatusApiCall, loading: creatingStatus } = useEvolvePost('shop/shipment/status');
  const addStatus = useCallback<ShipmentStatusesContextType['addStatus']>(
    async (body) => {
      const created = await createStatusApiCall({
        shipmentStateId: body.stateId,
        name: body.name,
        color: body.color,
      });
      setShipmentStatesData((current) =>
        current?.map((state) =>
          state.shipmentStateId === created.shipmentStateId
            ? { ...state, statuses: [...state.statuses, created] }
            : state,
        ),
      );
      return normalizeStatus(created);
    },
    [createStatusApiCall, setShipmentStatesData],
  );

  const { apiCall: editStatusApiCall, loading: editingStatus } = useEvolvePut('shop/shipment/status/:shipmentStatusId');
  const updateStatus = useCallback<ShipmentStatusesContextType['updateStatus']>(
    async (statusId, body: { name: string; color: HexTriplet }) => {
      const updated = await editStatusApiCall(body, {
        url: `shop/shipment/status/${statusId}`,
      });
      setShipmentStatesData((current) =>
        current?.map((state) =>
          state.shipmentStateId === updated.shipmentStateId
            ? {
                ...state,
                statuses: state.statuses.map((status) => (status.shipmentStatusId === statusId ? updated : status)),
              }
            : state,
        ),
      );
      return normalizeStatus(updated);
    },
    [editStatusApiCall, setShipmentStatesData],
  );

  const { apiCall: deleteStatusApiCall, loading: deletingStatus } = useEvolveDelete(
    'shop/shipment/status/:shipmentStatusId',
  );
  const deleteStatus = useCallback<ShipmentStatusesContextType['deleteStatus']>(
    async (statusId: ShipmentStatusId) => {
      await deleteStatusApiCall({
        url: `shop/shipment/status/${statusId}`,
      });
      setShipmentStatesData((current) =>
        current?.map((state) => ({
          ...state,
          statuses: state.statuses.filter((status) => status.shipmentStatusId !== statusId),
        })),
      );
    },
    [deleteStatusApiCall, setShipmentStatesData],
  );

  const mutationRunning = creatingStatus || editingStatus || deletingStatus;

  const value = useMemo<ShipmentStatusesContextType>(
    () => ({ states, statuses, statusMap, loading, addStatus, updateStatus, deleteStatus, mutationRunning }),
    [addStatus, deleteStatus, loading, mutationRunning, states, statusMap, statuses, updateStatus],
  );

  return <ShipmentStatusesContext value={value}>{children}</ShipmentStatusesContext>;
};

export const useShipmentStatuses = () => useGeneralContext(ShipmentStatusesContext, 'ShipmentStatuses');
